import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { BetSelection } from '@/constants';
import { globals } from '@/globals';
import { useAppStore } from '@/stores/app';
import { isStoreExistByCurrentRoute, useStoreByCurrentRoute } from '@/stores/composables/useStoreByCurrentRoute';
import { useUserStore } from '@/stores/user';
import type { IPageStoreWithFilters } from '@/types/page';

export function getCountryFlagUrl(iso3: string) {
    // return new URL(`/src/assets/images/flags/countries/${iso3}.png`, import.meta.url).href;
    return `/images/flags/countries/${iso3}.png`;
}

export function getTeamLogoUrl(logoName: string, updatedAt?: string | number | null) {
    // return new URL(`/src/assets/teams/${logoName}`, import.meta.url).href;
    const base = `/teams/${logoName}`;
    if (updatedAt == null || updatedAt === '') {
        return base;
    }
    if (typeof updatedAt === 'number') {
        return `${base}?_=${updatedAt}`;
    }
    const ms = Date.parse(updatedAt.includes('T') ? updatedAt : updatedAt.replace(' ', 'T'));
    return Number.isFinite(ms) ? `${base}?_=${ms}` : base;
}

export function getTwitterUrl(value: string | null | undefined): string | null {
    if (!value) return null;
    const handle = stripTwitterHandle(value);
    if (!handle) return null;
    return `https://x.com/${handle}`;
}

/** Handle without leading `@` — for form input when `@` is shown as prepend-inner. */
export function stripTwitterHandle(value: string | null | undefined): string {
    if (!value?.trim()) return '';
    return value.trim().replace(/^@+/, '');
}

/** DB/API value: `@Arsenal` or `null` when empty. */
export function formatTwitterHandle(value: string | null | undefined): string | null {
    const handle = stripTwitterHandle(value);
    if (!handle) return null;
    return `@${handle}`;
}

export function formatOdds(odds: string | number, precision = 3) {
    //if (!odds) return '';

    //let oddsArr = (odds + '').split('.');
    //return oddsArr[0] + '.' + (oddsArr[1] ? oddsArr[1].slice(0, precision).padEnd(precision, '0') : ''.padStart(precision, '0'));

    //faster
    const x = 10 ** precision;
    return (Math.trunc(Number(odds) * x) / x).toFixed(precision);
}

export function formatMoney(value: string | number, precision = 2) {
    return Number(value)
        .toFixed(precision)
        .replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

export function formatTime(dateString: string): string {
    if (!dateString) return '';
    const match = dateString.match(/(\d{1,2}):(\d{2}):(\d{2})/);
    if (match) {
        return match[0];
    }
    try {
        const date = new Date(dateString);
        const hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    } catch {
        return '';
    }
}

export function dateFormatInUserTimeZone(date: Date | string | number, formatStr: string): string {
    const userStore = useUserStore();
    const timeZone = userStore.isLoggedIn() ? userStore.timeZone : 'Europe/London';

    //if date instanceof Date, we assume that this date was created in JS in local time and no need to convert time to fromZonedTime
    if (!(date instanceof Date)) {
        date = fromZonedTime(date, 'Europe/London'); //'Europe/London' here is our DB timezone
    }

    return formatInTimeZone(date, timeZone, formatStr, { locale: userStore.dateFnsLocale });
}

export function playSound(url: string) {
    const audio = new Audio(url);
    audio.play();
}

export function getWebSocketURL(token: string) {
    const appStore = useAppStore();
    return (
        (location.protocol === 'http:' ? 'ws' : 'wss') +
        '://' +
        location.hostname +
        ':' +
        location.port +
        '/ws?token=' +
        token +
        '&appId=' +
        appStore.uniqAppId /* +
            (store.state.AppModule.isInitialized === false ? '&init=true' : '')*/
    );
}

export function pageReload(pageName: string | string[] | null = null) {
    const currentRouteName = globals.router.currentRoute.value.name;
    const pageNames = pageName == null ? null : Array.isArray(pageName) ? pageName : [pageName];

    if (pageNames && isStoreExistByCurrentRoute()) {
        const currentPageStore = useStoreByCurrentRoute<IPageStoreWithFilters<unknown>>();
        const isCurrentPageMatched = pageNames.some((page) => page === currentRouteName || page === currentPageStore.storeId);

        if (isCurrentPageMatched && currentPageStore.loadData) {
            currentPageStore.loadData();
            return;
        }
    } else {
        window.location.reload();
    }
}

export function getHandicapKeyByBetSelection(betSelectionId: number): string | null {
    if ([BetSelection.HandicapHome, BetSelection.HandicapAway].includes(betSelectionId)) return 'hdV'; // Asian Handicap
    if (
        [
            BetSelection.GoalOver,
            BetSelection.GoalUnder,
            BetSelection.GoalHomeOver,
            BetSelection.GoalHomeUnder,
            BetSelection.GoalAwayOver,
            BetSelection.GoalAwayUnder,
        ].includes(betSelectionId)
    )
        return 'ouV'; // Over/Under
    // if ([BetSelection.CornersHandicapHome, BetSelection.CornersHandicapAway].includes(betSelectionId)) return 7; // Corners Asian Handicap
    // if ([BetSelection.CornersGoalOver, BetSelection.CornersGoalUnder].includes(betSelectionId)) return 8; // Corners Over/Under

    return null;
}

export function sleep(time: number) {
    return new Promise((resolve) => setTimeout(resolve, time));
}

export function formatNumber(value: number) {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

export function formatGroupedNumber(value: unknown): string {
    if (value == null || value === '') return '';
    const n = typeof value === 'number' ? value : Number(String(value).trim());
    if (!Number.isFinite(n)) return String(value);
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 20,
    }).format(n);
}

/** Grouped number without fractional part. */
export function formatGroupedInteger(value: unknown): string {
    if (value == null || value === '') return '';
    const n = typeof value === 'number' ? value : Number(String(value).trim());
    if (!Number.isFinite(n)) return String(value);
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Math.trunc(n));
}

export function formatElapsed(dateStr: string | null): string {
    if (!dateStr) return '';
    const diffMs = Date.now() - fromZonedTime(dateStr, 'Europe/London').getTime();
    if (diffMs < 0) return '';
    const totalMin = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMin / (24 * 60));
    const hours = Math.floor((totalMin % (24 * 60)) / 60);
    const minutes = totalMin % 60;
    let result = '';
    if (days) result += `${days}d `;
    if (hours) result += `${hours}h `;
    result += `${minutes}m`;
    return result;
}
